import React, {
    lazy,
    ReactElement,
    useContext,
    useEffect,
    useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AccountPortal } from '@sections/index';
import ProfileService from '@services/profile-service/profile-service';
import { VehicleAttributes } from '@models/vehicle-attributes';
import { VehicleTabs } from '@sections/account-portal/components/vehicle-tabs/vehicle-tabs';
import AuthenticationService from '@services/authentication-service/authentication-service';
import { ActivityIndicator } from '@common/activity-indicator/activity-indicator';
import ErrorPage from '@sections/profile-error/error-page';
import { useWindowSize } from '@hooks/use-window-size';
import { CacheService } from '@services/cache-service/cache-service';
import ServerContext from '@contexts/serverContext';
import { useAppInformationContent } from '@sections/breadcrumbs/hook/use-breadcrumb-content';
import seoService from '@services/seo-service/seo-service';
import { TABLET_BREAKPOINT } from '@services/support-constants';
import { useMastheadContent } from '@sections/account-portal/hooks/use-masthead-content';
import { useVehicleCarouselContent } from '@sections/account-portal/components/vehicle-tabs/hooks/use-vehicle-carousel-content';
import { useAddVehicleContent } from '@sections/add-vehicle/hooks/use-add-vehicle';
import OrdersService, {
    OrderedVehiclesResponse,
} from '@services/orders-service/orders-service';
import {
    CONFIG_FOR_ORDERS_TO_FILTER_OUT,
    OrderedVehiclesItem,
} from '@models/orders-and-reservations';
import AppConfigurationService from '@services/app-configuration-service/app-configuration-service';
import './account-dashboard-view.scss';
import {
    DynamicMastheadContent,
    DynamicMastheadGreetingStyle,
    DynamicMastheadProperties,
    useDynamicMastheadContent,
} from '@sections/account-portal/components/vehicle-tabs/hooks/use-dynamic-masthead-content';
import {
    BRAND,
    CANCELLED_ORDER_STATUSES,
    CX740_CARD,
    displayHeaderAndFooter,
    EU_COUNTRIES,
    hasElectricVehicle,
    LinkTargetValues,
    pauseFooter,
    PREFERRED_DEALER_CONSENT_KEY,
    VEHICLE_CARD_STORAGE_KEY,
} from '@/constants';
import { useCongratsModalContent } from '@views/account-dashboard-view/hooks/use-congrats-modal-content';
import { ModalContextProps, useModalContext } from '@contexts/modalContext';
import { ORDER_TYPE } from '@sections/account-portal/components/vehicle-carousel/vehicle-order-card/vehicle-order-card';
import HttpService from '@services/http-service/http-service';
import { findPathByAlias } from '@routes/routesList';
import {
    buildCampaignIdQueryParameter,
    buildChargerAdapterStatusQueryParameter,
    buildConnectedServicesExtraQueryParams,
} from '@utils/router-util/router-util';
import { usePreferredDealerModalContent } from '@smart-tiles/smart-tile-preferred-dealer/hook/use-preferred-dealer-modal-content';
import { usePreferredDealerDrawerContent } from '@views/preferred-dealer-view/hooks/use-preferred-dealer-drawer-content';
import { useConnectedServicesModalContent } from '@smart-tiles/smart-tile-connected-services/hook/use-connected-services-modal-content';
import DeletedVehicleService from '@services/deleted-vehicle-service/deleted-vehicle-service';
import OmsService, {
    OmsOrderHistoryResponse,
    OrderHistoryOrder,
} from '@services/oms-service/oms-service';
import { filterForActiveOrders } from '@views/order-details-view/nvc-status-map';
import PreferredDealerConsentService from '@/services/preferred-dealer-consent-service/preferred-dealer-consent-service';
import { useDealerConsentContent } from '@/components/sections/preferred-dealer-consent/hooks/use-dealer-consent';
import PreferredDealerConsentContent from '@/components/sections/preferred-dealer-consent/preferred-dealer-consent-modal';
import {
    Device,
    PrivacyPreferenceRequest,
    PrivacyPreferencesRequestData,
} from '@/models/preferred-dealer-consent';
import {
    NotificationType,
    useNotificationContext,
} from '@contexts/notificationContext';
import { useConnectedStatusContext } from '@contexts/connectedStatusContext';
import { useAnalytics } from '@/hooks/use-analytics';
import { useNotificationBannerContent } from '@sections/notification-banner/hooks/use-notification-banner-content';
import VehicleImageService from '@sections/account-portal/services/vehicle-image-service/vehicle-image-service';
import { VehicleDetail } from '@models/profile-with-vehicles';
import { scriptService } from '@services/script-service/script-service';
import ZoomResetUtil from '@/components/utils/zoom-reset/zoom-reset';
import serverSideService from '@services/server-side-service/server-side-service';
import { checkIfOrderIsDelivered } from '@sections/active-orders-carousel/util/order-status-util';
import { EU_POST_REDIRECT_COUNTRIES_NAME } from '@utils/values-by-country/value-by-country';

const Notification = lazy(async () => ({
    default: (
        await import(
            '@sections/account-portal/components/notification-message/notification'
        )
    ).Notification,
}));

const AccountDashboardView = () => {
    const navigate = useNavigate();
    const [fireAnalytics] = useAnalytics();
    const { setContext, resetContext } = useModalContext();
    const { getConnectedStatus } = useConnectedStatusContext();
    const { setNotificationContext } = useNotificationContext();

    const size = useWindowSize();
    const [isLoading, setIsLoading] = useState(false);

    const [dynamicMastheadProperties, setDynamicMastheadProperties] =
        useState<DynamicMastheadProperties>({
            hasDynamicMasthead: false,
            orientation: null,
        });

    const appConfig: AppConfigurationService = new AppConfigurationService();
    const authenticationService = new AuthenticationService();
    const profileService = new ProfileService();
    const cacheService = new CacheService();
    const httpService = HttpService;
    const ordersService: OrdersService = new OrdersService(httpService);
    const deletedVehicleService = new DeletedVehicleService(httpService);
    const preferredDealerConsentService = new PreferredDealerConsentService(
        httpService
    );
    const omsService = new OmsService(httpService);

    const notificationBannerContent = useNotificationBannerContent();
    const preferredDealerModalContent = usePreferredDealerModalContent();
    const preferredDealerDrawerContent = usePreferredDealerDrawerContent();
    const connectedServicesModalContent = useConnectedServicesModalContent();
    const congratsModalContent = useCongratsModalContent();
    const mastheadContent = useMastheadContent();
    const vehicleCarouselContent = useVehicleCarouselContent();
    const addVehicleContent = useAddVehicleContent();
    const dealerConsentContent = useDealerConsentContent();
    const appInformationContent = useAppInformationContent();
    const dynamicMastheadContent: DynamicMastheadContent =
        useDynamicMastheadContent(appConfig.currentCountryCode);

    const [selectedDynamicMasthead, setSelectedDynamicMasthead] =
        useState<DynamicMastheadGreetingStyle>(null);

    const [isMobile, setIsMobile] = useState(size?.width < 600);
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

    const [vehiclesData, setVehiclesData] = useState<VehicleAttributes[]>([]);
    const [email, setEmail] = useState<string>();
    const [profileError, setProfileError] = useState({
        status: false,
        message: '',
    });
    const [vehiclesOnOrder, setVehiclesOnOrder] = useState<
        OrderedVehiclesItem[]
    >([]);
    const [vehiclesCount, setVehiclesCount] = useState<number>(0);
    const [currentVehiclesCount, setCurrentVehiclesCount] =
        useState<number>(null);
    const [deletedVehicleState, setDeletedVehicleState] =
        useState<boolean>(false);
    const [hasCX740Reservation, setHasCX740Reservation] =
        useState<boolean>(false);
    const [hasCX740Order, setHasCX740Order] = useState<boolean>(false);
    const [displayAddVehicleModal, handleAddVehicleModal] =
        useState<boolean>(false);
    const [newVehicleState, setIsNewVehicleAdded] = useState<boolean>(false);
    const [profileRequestCompleted, setProfileRequestCompleted] =
        useState<boolean>(false);
    const [
        ordersReservationsRequestCompleted,
        setOrdersReservationsRequestCompleted,
    ] = useState<boolean>(false);
    const [userGuid, setUserGuid] = useState<string>(null);

    const [deliveredVehicles, setDeliveredVehicles] = useState<
        OrderedVehiclesItem[]
    >([]);
    const [userHasElectricVehicle, setUserHasElectricVehicle] =
        useState<boolean>(false);

    const [activeNvcOrders, setActiveNvcOrders] =
        useState<OrderHistoryOrder[]>(null);
    const [orderHistoryData, setOrderHistoryData] =
        useState<OmsOrderHistoryResponse>(null);
    const [orderedVehiclesResponse, setOrderedVehiclesResponse] =
        useState<OrderedVehiclesResponse>(null);

    const [notificationStatus, setNotificationStatus] =
        useState<NotificationType>(null);
    const [
        chargerAdapterNotificationStatus,
        setChargerAdapterNotificationStatus,
    ] = useState<boolean>(false);

    const dashboardIsReady = Boolean(
        email && size.width && mastheadContent && vehicleCarouselContent
    );
    const isFord = appConfig.brand === 'ford';
    const isLincoln = appConfig.brand === 'lincoln';
    const countryCode = appConfig.get2LetterCountryCode().toLowerCase();
    const isEu = EU_COUNTRIES.includes(countryCode);
    const isCanada = appConfig.currentCountryCode === 'ca';
    const postRedirectEligible = EU_POST_REDIRECT_COUNTRIES_NAME.map(
        (option) => option.value
    ).includes(appConfig.get3LetterCountryCode());

    const queryParams = new URLSearchParams(useLocation().search);
    const hasConnectedServicesParameter: string =
        queryParams.get('connectedservices') || undefined;
    const hasAddVehicleParameter = queryParams.get('addvehicle') || undefined;
    const hasPreferredDealerParameter =
        queryParams.get('preferredDealer') || undefined;
    const chargerAdapterStatus = queryParams.get('keyName') || undefined;

    authenticationService.updateState(findPathByAlias('AccountDashboardView'));

    const serverContext = useContext(ServerContext);
    seoService.set(
        {
            pageTitle: appInformationContent?.accountDashboardPageTitle,
            applicationName: appInformationContent?.applicationName,
            pageDescription:
                appInformationContent?.accountDashboardPageDescription,
        },
        serverContext
    );

    const congratsModalProps: ModalContextProps = {
        modalType: {
            name: 'congrats-modal',
            primaryButtonLabel: congratsModalContent?.primaryButtonLabel,
            primaryButtonAriaLabel:
                congratsModalContent?.primaryButtonAriaLabel,
            closeButtonAriaLabel: congratsModalContent?.closeButtonAriaLabel,
            role: 'dialog',
            onPrimaryButtonClick: () => {
                closeCongratsModal();
                resetContext();
            },
            onAfterClose: () => {
                closeCongratsModal();
                resetContext();
            },
            children: (
                <>
                    <h2 className="fmc-type--heading3 fds-color__text--primary">
                        {congratsModalContent?.headerText}
                    </h2>

                    <div
                        className="congrats-modal-body fmc-type--body1 fds-color__text--gray3"
                        dangerouslySetInnerHTML={{
                            __html: congratsModalContent?.bodyText,
                        }}
                    ></div>
                </>
            ),
        },
    };

    const goToEuPostRegistration = (): void => {
        navigate(findPathByAlias('EuPostRegistrationRedirect'), {
            state: {
                postRegistration: true,
            },
        });
    };

    const setDealerConsent = (
        vinWithoutConsent: any,
        consentStatus: boolean
    ) => {
        const privacyPreferencesRequestData: PrivacyPreferencesRequestData =
            {} as PrivacyPreferencesRequestData;
        privacyPreferencesRequestData.brandCode =
            BRAND[appConfig.getBrand()].SHORT_NAME;
        privacyPreferencesRequestData.countryCode = appConfig
            .get3LetterCountryCode()
            .toUpperCase();
        privacyPreferencesRequestData.guid = userGuid;
        privacyPreferencesRequestData.privacyPreferences = [];
        const privacyPreferenceRequest: PrivacyPreferenceRequest =
            {} as PrivacyPreferenceRequest;
        privacyPreferenceRequest.consentName = PREFERRED_DEALER_CONSENT_KEY;
        privacyPreferenceRequest.statusCode = consentStatus ? 'Y' : 'N';
        privacyPreferenceRequest.preferredDealerId =
            vinWithoutConsent.preferredDealer;
        privacyPreferenceRequest.devices = [];
        const device: Device = {} as Device;
        device.deviceType = 'VIN';
        device.deviceValue = vinWithoutConsent.vin;
        privacyPreferenceRequest.devices.push(device);
        privacyPreferencesRequestData.privacyPreferences.push(
            privacyPreferenceRequest
        );
        preferredDealerConsentService
            .updatePreferredDealerConsent(privacyPreferencesRequestData)
            .then((response) => {
                if (response) {
                    isFord && setNotificationStatus(NotificationType.Success);
                    isLincoln &&
                        setNotificationContext(
                            NotificationType.DealerConsentSuccess,
                            false
                        );
                }
                resetContext();
                validatePreferredDealerConsents();
            })
            .catch((err) => {
                resetContext();
            });
    };

    const createDealerConsentModalProps = (vinWithoutConsent?: any) => {
        return {
            modalType: {
                name: 'preferred-dealer-consent-modal',
                primaryButtonLabel: dealerConsentContent.acceptButtonLabelText,
                secondaryButtonLabel:
                    dealerConsentContent.declineButtonLabelText,
                onPrimaryButtonClick: () => {
                    const consentSelection = 'accept';
                    fireAnalytics(
                        'accountDashboardDealerConsentOnclickEvent',
                        '',
                        { consentSelection }
                    );
                    setDealerConsent(vinWithoutConsent, true);
                },
                onSecondaryButtonClick: () => {
                    const consentSelection = 'decline';
                    fireAnalytics(
                        'accountDashboardDealerConsentOnclickEvent',
                        '',
                        { consentSelection }
                    );
                    setDealerConsent(vinWithoutConsent, false);
                },
                preventClose: true,
                children: (
                    <PreferredDealerConsentContent
                        preferredDealerConsentContent={dealerConsentContent}
                        vinWithoutConsent={vinWithoutConsent}
                    />
                ),
            },
        };
    };

    const isCompletedAndDelivered = (order: OrderedVehiclesItem): boolean => {
        return (
            EU_COUNTRIES.includes(countryCode) &&
            order.status === ORDER_TYPE.COMPLETED
        );
    };

    const addToGarageIfDelivered = async (
        order: OrderedVehiclesItem
    ): Promise<void> => {
        if (
            (checkIfOrderIsDelivered(order, appConfig) &&
                order.status !== ORDER_TYPE.ORDERED) ||
            isCompletedAndDelivered(order)
        ) {
            const deletedVehicles =
                await deletedVehicleService.getDeletedVehicles();
            if (
                deletedVehicles?.deletedVehicles &&
                !deletedVehicles.deletedVehicles?.some(
                    (deletedVehicle) => deletedVehicle.vin === order.vin
                )
            ) {
                setDeliveredVehicles((deliveredVehicles) => [
                    ...deliveredVehicles,
                    order,
                ]);
            }
        }
    };

    const isValidOrder = (order: OrderedVehiclesItem): boolean => {
        return (
            !CANCELLED_ORDER_STATUSES.includes(order.status?.toUpperCase()) &&
            !CANCELLED_ORDER_STATUSES.includes(
                order.vehicleStatusCode?.toUpperCase()
            ) &&
            !checkIfOrderIsDelivered(order, appConfig) &&
            !order.showroomJourneyType &&
            order.status !== ORDER_TYPE.COMPLETED
        );
    };

    const isCX740 = (order: OrderedVehiclesItem): boolean => {
        const isCX740Reservation =
            order.orderType?.toUpperCase() === ORDER_TYPE.RESERVATION &&
            order.readyToOrder === false &&
            order.model === CX740_CARD;
        const isCX740Order =
            order.orderType.toUpperCase() === ORDER_TYPE.ORDER &&
            order.status === ORDER_TYPE.ORDERED &&
            order.readyToOrder === false &&
            order.model === CX740_CARD;
        const isContractRequestedCX740Order =
            order.orderType.toUpperCase() === ORDER_TYPE.ORDER &&
            order.status === ORDER_TYPE.CONTRACT_REQUESTED &&
            order.readyToOrder === false &&
            order.model === CX740_CARD;

        if (isCX740Order || isContractRequestedCX740Order) {
            setHasCX740Order(true);
        } else if (isCX740Reservation) {
            setHasCX740Reservation(true);
        }

        return (
            isCX740Reservation || isCX740Order || isContractRequestedCX740Order
        );
    };

    const filterOrdersByAuthoring = (
        orders: OrderedVehiclesItem[]
    ): OrderedVehiclesItem[] => {
        if (vehicleCarouselContent) {
            const {
                eComVehicleCardOrderTitle,
                eComVehicleCardReservationTitle,
            } = vehicleCarouselContent;

            if (eComVehicleCardOrderTitle && eComVehicleCardReservationTitle) {
                return orders.filter((order) => isValidOrder(order));
            } else if (eComVehicleCardOrderTitle) {
                return orders.filter(
                    (order) =>
                        isValidOrder(order) &&
                        order.orderType?.toUpperCase() === ORDER_TYPE.ORDER
                );
            } else if (eComVehicleCardReservationTitle) {
                return orders.filter(
                    (order) =>
                        isValidOrder(order) &&
                        order.orderType?.toUpperCase() ===
                            ORDER_TYPE.RESERVATION
                );
            }
        }
    };

    const preloadVehicleImages = (
        vehicles: VehicleDetail[] | OrderedVehiclesItem[]
    ): void => {
        if (!document) return;

        const isGarageVehicles = vehicles.some((vehicle) =>
            Boolean(vehicle.modelYear)
        );
        const vehicleImageService = new VehicleImageService();

        vehicles.forEach(async (vehicle) => {
            const link = document.createElement('link');
            link.rel = 'preload';
            link.as = 'image';
            link.crossOrigin = 'anonymous';

            const response = await vehicleImageService.getVehicleImage(
                {
                    vin: vehicle.vin,
                    year: Number(
                        isGarageVehicles ? vehicle.modelYear : vehicle.year
                    ),
                    make: vehicle.make,
                    model: vehicle.modelName,
                },
                undefined,
                appConfig.get3LetterCountryCode().toUpperCase()
            );

            if (response) {
                link.href = response;

                if (document) document.head.appendChild(link);
            }
        });
    };

    const getAndSetProfileVehiclesData = () => {
        cacheService.evictProfileLiteCache();
        authenticationService
            .onIsAuthenticated()
            .then((isAuthenticated: boolean) => {
                if (isAuthenticated) {
                    setIsAuthenticated(true);
                    profileService
                        .requestLite()
                        .then((profile) => {
                            if (profile) {
                                setEmail(profile.profile.email);
                                setUserGuid(
                                    profile.profile.userGuid.toUpperCase()
                                );

                                if (profile.vehicles.length > 0) {
                                    // Check if the user's newest vehicle is connected
                                    preloadVehicleImages(profile.vehicles);
                                    getConnectedStatus(profile, false);
                                    setUserHasElectricVehicle(
                                        hasElectricVehicle(profile.vehicles)
                                    );
                                    setVehiclesCount(profile.vehicles.length);
                                    setCurrentVehiclesCount(
                                        profile.vehicles.length
                                    );
                                    const sortedVehicleProfiles =
                                        profile.vehicles;
                                    sortedVehicleProfiles.sort(
                                        (vehicle1, vehicle2) =>
                                            parseInt(vehicle2.modelYear!, 10) -
                                            parseInt(vehicle1.modelYear!, 10)
                                    );
                                    setVehiclesData(
                                        sortedVehicleProfiles.map((vehicle) => {
                                            return {
                                                year: parseInt(
                                                    vehicle.modelYear,
                                                    10
                                                ),
                                                make: vehicle.make,
                                                model: vehicle.modelName,
                                                vin: vehicle.vin,
                                                preferredDealer:
                                                    vehicle.preferredDealer,
                                                nickName: vehicle.nickName,
                                                ownerState: vehicle.ownerState,
                                                engineType: vehicle.engineType,
                                                isConnected:
                                                    vehicle.isConnected,
                                            };
                                        })
                                    );
                                } else {
                                    setCurrentVehiclesCount(0);
                                }
                                setProfileRequestCompleted(true);
                            }
                        })
                        .catch((err) => {
                            cacheService.evictProfileLiteCache();
                            setProfileError({
                                status: true,
                                message: err.message,
                            });
                        });
                } else {
                    cacheService.evictProfileLiteCache();
                    authenticationService.login();
                }
            });
    };

    useEffect(() => {
        getAndSetProfileVehiclesData();
    }, []);

    useEffect(() => {
        if (
            isAuthenticated &&
            vehicleCarouselContent &&
            (vehicleCarouselContent.eComVehicleCardOrderTitle ||
                vehicleCarouselContent.eComVehicleCardReservationTitle)
        ) {
            ordersService
                .getOrderedAndReservedVehicles()
                .then((response: OrderedVehiclesResponse) => {
                    if (response.orders.length) {
                        setOrderedVehiclesResponse(response);
                    }
                })
                .catch((e) => {
                    throw new Error(e);
                })
                .finally(() => setOrdersReservationsRequestCompleted(true));
        } else {
            setOrdersReservationsRequestCompleted(true);
        }
    }, [
        isAuthenticated,
        vehicleCarouselContent?.eComVehicleCardOrderTitle,
        vehicleCarouselContent?.eComVehicleCardReservationTitle,
    ]);

    useEffect(() => {
        if (userGuid && orderedVehiclesResponse) {
            if (orderedVehiclesResponse.orders.length) {
                const selectedUser = CONFIG_FOR_ORDERS_TO_FILTER_OUT.find(
                    (order) => order.userGuid === userGuid
                );
                let ordersResponse: OrderedVehiclesItem[];

                if (selectedUser) {
                    ordersResponse = orderedVehiclesResponse.orders.filter(
                        (order) => order.vin !== selectedUser.vin
                    );
                } else {
                    ordersResponse = orderedVehiclesResponse.orders;
                }

                ordersResponse.forEach((order) => {
                    addToGarageIfDelivered(order);
                });
                const ordersFilteredByAuthoring: OrderedVehiclesItem[] =
                    filterOrdersByAuthoring(ordersResponse);
                ordersResponse.forEach((order) => isCX740(order));
                setVehiclesOnOrder(ordersFilteredByAuthoring);
            }
        }
    }, [userGuid, orderedVehiclesResponse]);

    useEffect(() => {
        if (isAuthenticated && postRedirectEligible)
            preferredDealerConsentService
                .getPreferredDealerConsentResults(
                    appConfig.get3LetterCountryCode().toUpperCase()
                )
                .then((response) => {
                    if (response?.value.orgLevelPrivacies === null) {
                        goToEuPostRegistration();
                    }
                });
    }, [isAuthenticated]);

    useEffect(() => {
        if (vehiclesOnOrder?.length && serverSideService.isClientSide()) {
            preloadVehicleImages(vehiclesOnOrder);
        }
    }, [vehiclesOnOrder]);

    useEffect(() => {
        if (
            isAuthenticated &&
            vehicleCarouselContent?.activeOrdersHeader &&
            countryCode === 'us'
        ) {
            omsService
                .getOrderHistory('1', '10', appConfig.brand)
                .then((response) => {
                    if (response) {
                        setOrderHistoryData(response);
                    }

                    if (response?.orders?.length) {
                        setActiveNvcOrders(
                            filterForActiveOrders(response?.orders)
                        );
                    }
                });
        }
    }, [isAuthenticated, vehicleCarouselContent?.activeOrdersHeader]);

    useEffect(() => {
        if (
            ordersReservationsRequestCompleted &&
            profileRequestCompleted &&
            deliveredVehicles.length > 0
        ) {
            addDeliveredVehiclesToGarage();
        }
        if (vehiclesCount && vehiclesCount > 0 && dealerConsentContent) {
            validatePreferredDealerConsents();
        }
    }, [
        ordersReservationsRequestCompleted,
        profileRequestCompleted,
        deliveredVehicles,
    ]);

    const addDeliveredVehiclesToGarage = () => {
        const garageVehiclesVinArray = vehiclesData.map(
            (vehicle) => vehicle.vin
        );

        const deliveredVehiclesNotInGarage = deliveredVehicles.filter(
            (deliveredVehicle) =>
                !garageVehiclesVinArray.includes(deliveredVehicle.vin)
        );

        const addVehiclePromises = [];
        for (const deliveredVehicle of deliveredVehiclesNotInGarage) {
            //add VIN(s) to profile (garage)
            addVehiclePromises.push(
                profileService.addVehicle(deliveredVehicle.vin, '')
            );
        }
        Promise.all(addVehiclePromises).then((promises) => {
            if (promises.some((response) => response.status === 200)) {
                getAndSetProfileVehiclesData();
                setIsNewVehicleAdded(true);
                showDeliveredVehicleCongratsModal();
            }
        });
    };

    const showDeliveredVehicleCongratsModal = (): void => {
        if (congratsModalContent) setContext(congratsModalProps);
    };

    const closeCongratsModal = (): void => {
        resetContext();
    };

    const generateTempRTEForTeslaBanner = (): ReactElement => {
        const splitLabelText =
            notificationBannerContent?.specialAlertCtaLabelText.split(' ');
        const href = notificationBannerContent?.specialAlertCtaHref;
        const ariaLabel = notificationBannerContent?.specialAlertCtaAriaLabel;
        const languageRegionCodeIsNonEnglish =
            appConfig.currentLanguageCode !== 'en';
        const linkText = languageRegionCodeIsNonEnglish
            ? `${splitLabelText[0]} ${splitLabelText[1]} ${splitLabelText[2]}`
            : `${splitLabelText[0]} ${splitLabelText[1]}`;
        const indexToBeginRestOfText = languageRegionCodeIsNonEnglish ? 3 : 2;

        return (
            <p>
                <a
                    href={href}
                    aria-label={ariaLabel}
                    target={LinkTargetValues.BLANK}
                >
                    {isCanada
                        ? notificationBannerContent?.specialAlertCtaLabelText
                        : linkText}
                </a>{' '}
                {!isCanada
                    ? splitLabelText.slice(indexToBeginRestOfText).join(' ')
                    : null}
            </p>
        );
    };

    const closeChargerAdapterBanner = (): void => {
        setChargerAdapterNotificationStatus(null);
    };

    const handleConnectedServiceView = (vin: string): void => {
        navigate({
            pathname: findPathByAlias('ConnectedServicesView'),
            search: `?vin=${vin}${buildCampaignIdQueryParameter()}${buildChargerAdapterStatusQueryParameter(
                !!chargerAdapterStatus
            )}${buildConnectedServicesExtraQueryParams(queryParams)}`,
        });
    };

    const handlePreferredDealerView = (): void => {
        sessionStorage.setItem(
            VEHICLE_CARD_STORAGE_KEY,
            JSON.stringify({
                vin: vehiclesData[0]?.vin,
                model: `${vehiclesData[0]?.year} ${vehiclesData[0]?.model}`,
                preferredDealerCode: vehiclesData[0]?.preferredDealer,
            })
        );
        navigate({
            pathname: findPathByAlias('PreferredDealerView'),
        });
    };

    const validatePreferredDealerConsents = (): void => {
        if (email) {
            preferredDealerConsentService
                .getPreferredDealerConsentResults(
                    appConfig.get3LetterCountryCode().toUpperCase()
                )
                .then((response) => {
                    if (response?.value?.orgLevelPrivacies?.length > 0) {
                        response.value.orgLevelPrivacies.forEach((item) => {
                            if (item.privacyPreferences.length > 0) {
                                const preferredDealerConsents =
                                    item.privacyPreferences.filter(
                                        (consent) =>
                                            consent.consentName ==
                                            PREFERRED_DEALER_CONSENT_KEY
                                    );
                                if (
                                    preferredDealerConsents?.length > 0 ||
                                    vehiclesData.length > 0
                                ) {
                                    const availableConsents = [];
                                    const vinsWithoutConsent = [];
                                    preferredDealerConsents.forEach(
                                        (consent) => {
                                            consent.devices.forEach(
                                                (device) => {
                                                    device[
                                                        'preferredDealerId'
                                                    ] =
                                                        consent.preferredDealerId;
                                                    availableConsents.push(
                                                        device
                                                    );
                                                }
                                            );
                                        }
                                    );
                                    vehiclesData?.forEach((vehicle) => {
                                        if (vehicle.preferredDealer) {
                                            const isConsentPresent =
                                                availableConsents.some(
                                                    (consent) =>
                                                        vehicle.vin ==
                                                        consent.deviceValue
                                                );
                                            const isConsentPresentforDiffPD =
                                                availableConsents.some(
                                                    (consent) =>
                                                        vehicle.vin ==
                                                            consent.deviceValue &&
                                                        vehicle.preferredDealer !=
                                                            consent.preferredDealerId
                                                );

                                            if (
                                                !isConsentPresent ||
                                                isConsentPresentforDiffPD
                                            ) {
                                                vinsWithoutConsent.push({
                                                    vin: vehicle.vin,
                                                    year: vehicle.year,
                                                    model: vehicle.model,
                                                    preferredDealer:
                                                        vehicle.preferredDealer,
                                                });
                                            }
                                        }
                                    });
                                    if (
                                        vinsWithoutConsent &&
                                        vinsWithoutConsent.length > 0
                                    ) {
                                        const dealerConsentModalProps =
                                            createDealerConsentModalProps(
                                                vinsWithoutConsent[0]
                                            );
                                        setContext(dealerConsentModalProps);
                                    }
                                }
                            }
                        });
                    }
                })
                .catch((err) => {
                    resetContext();
                });
        }
    };

    useEffect(() => {
        if (!deletedVehicleState) {
            if (
                window.location.href.includes('connectedservices') &&
                vehiclesData.length === 1
            ) {
                handleConnectedServiceView(vehiclesData[0]?.vin);
            } else if (
                window.location.href.includes('preferredDealer') &&
                vehiclesData.length === 1
            ) {
                handlePreferredDealerView();
            }
            if (
                !newVehicleState &&
                (hasAddVehicleParameter ||
                    ((hasPreferredDealerParameter ||
                        hasConnectedServicesParameter) &&
                        currentVehiclesCount === 0))
            ) {
                handleAddVehicleModal(true);
            }
        }
    }, [vehiclesData]);

    useEffect(() => {
        const isMobileView = size.width < TABLET_BREAKPOINT;
        setIsMobile(isMobileView);
    }, [size.width]);

    useEffect(() => {
        if (dynamicMastheadContent) {
            const hasDynamicMastheadAuthored =
                !!dynamicMastheadContent.options.length;

            const defaultMasthead: DynamicMastheadGreetingStyle =
                dynamicMastheadContent.options?.find(
                    (option) => option.key === 'default'
                );
            const hasReservedCX740Masthead: DynamicMastheadGreetingStyle =
                dynamicMastheadContent.options?.find(
                    (option) => option.key === 'hasReservedCX740'
                );
            const hasOrderedCX740Masthead: DynamicMastheadGreetingStyle =
                dynamicMastheadContent.options?.find(
                    (option) => option.key === 'hasOrderedCX740'
                );

            if (hasCX740Order && hasOrderedCX740Masthead) {
                setSelectedDynamicMasthead(hasOrderedCX740Masthead);
                setDynamicMastheadProperties({
                    hasDynamicMasthead: hasDynamicMastheadAuthored,
                    orientation: hasOrderedCX740Masthead.orientation,
                });
            } else if (hasCX740Reservation && hasReservedCX740Masthead) {
                setSelectedDynamicMasthead(hasReservedCX740Masthead);
                setDynamicMastheadProperties({
                    hasDynamicMasthead: hasDynamicMastheadAuthored,
                    orientation: hasReservedCX740Masthead.orientation,
                });
            } else {
                setSelectedDynamicMasthead(defaultMasthead);
                setDynamicMastheadProperties({
                    hasDynamicMasthead: hasDynamicMastheadAuthored,
                    orientation: defaultMasthead.orientation,
                });
            }
        }
    }, [dynamicMastheadContent, hasCX740Reservation, hasCX740Order]);

    useEffect(() => {
        isEu && (async () => await scriptService.loadGoogleMapsScript(null))();
    }, []);

    useEffect(() => {
        new ZoomResetUtil().resetZoom();
    }, []);
    useEffect(() => {
        dashboardIsReady ? displayHeaderAndFooter() : pauseFooter();
    }, [dashboardIsReady]);

    return (
        <>
            {(isLoading || !dashboardIsReady) && (
                <ActivityIndicator className={'full-height'} />
            )}
            {notificationStatus && (
                <div
                    className="preferred-dealer-consent__notification-container"
                    data-testid="preferred-dealer-consent__notification-container"
                >
                    <Notification
                        status={notificationStatus}
                        mainCopy={
                            dealerConsentContent.notificationBannerHeaderText
                        }
                        subCopy={
                            dealerConsentContent.notificationBannerBodyText
                        }
                        rteField={true}
                        hideBorder={true}
                        hideAfterTimeout={true}
                        onHideNotification={() => {
                            setNotificationStatus(null);
                        }}
                    />
                </div>
            )}
            {chargerAdapterNotificationStatus && (
                <div
                    className="charger-adapter-banner__notification-container"
                    data-testid="charger-adapter-banner__notification-container"
                >
                    <Notification
                        status={NotificationType.Info}
                        mainCopy={null}
                        subCopy={null}
                        ctaText={null}
                        rteField={false}
                        isTeslaBanner={true}
                        teslaBannerContent={generateTempRTEForTeslaBanner()}
                        hideBorder={true}
                        hideAfterTimeout={false}
                        onHideNotification={closeChargerAdapterBanner}
                        isCanada={isCanada}
                    />
                </div>
            )}
            {dashboardIsReady && (
                <AccountPortal
                    page="DASHBOARD"
                    hasVehicles={vehiclesCount > 0}
                    updateAddVehicleStatusWithVin={(vin: string) => {
                        // by updating this status, updated hasVehicles flag will be passed by prop drilling to manage the connected service smart tile dynamic button
                        setIsNewVehicleAdded(true);
                        handleConnectedServiceView(vin);
                    }}
                    fromConnectedServices={!!hasConnectedServicesParameter}
                    fromPreferredDealer={!!hasPreferredDealerParameter}
                    preferredDealerModalContent={preferredDealerModalContent}
                    preferredDealerDrawerContent={preferredDealerDrawerContent}
                    connectedServicesModalContent={
                        connectedServicesModalContent
                    }
                    mastheadContent={mastheadContent}
                    dynamicMastheadContent={selectedDynamicMasthead}
                    dynamicMastheadProperties={dynamicMastheadProperties}
                    vehicleCarouselContent={vehicleCarouselContent}
                    addVehicleContent={addVehicleContent}
                    openAddVehicleModal={displayAddVehicleModal}
                    vehiclesCount={vehiclesCount}
                    activeNvcOrders={activeNvcOrders}
                    vehiclesOnOrder={vehiclesOnOrder}
                    totalOrders={
                        (Number(orderHistoryData?.totalOrders) ?? 0) +
                        (orderedVehiclesResponse?.orders?.length ?? 0)
                    }
                    setIsLoading={setIsLoading}
                    currentVehiclesCount={currentVehiclesCount}
                    setCurrentVehiclesCount={setCurrentVehiclesCount}
                    garageVehicles={vehiclesData}
                    addVehicleFromSmartTile={displayAddVehicleModal}
                    selectedDynamicMasthead={selectedDynamicMasthead}
                    setDynamicMastheadProperties={setDynamicMastheadProperties}
                    setSelectedDynamicMasthead={setSelectedDynamicMasthead}
                    handleAddVehicleModal={handleAddVehicleModal}
                    setIsNewVehicleAdded={setIsNewVehicleAdded}
                    topMasthead={
                        <VehicleTabs
                            redirectToDashboard={false}
                            vehiclesCount={(count) => {
                                setVehiclesCount(count);
                            }}
                            isNewVehicleAdded={newVehicleState}
                            setIsNewVehicleAdded={setIsNewVehicleAdded}
                            openAddVehicleModal={displayAddVehicleModal}
                            addVehicleContent={addVehicleContent}
                            dynamicMastheadProperties={
                                dynamicMastheadProperties
                            }
                            setDynamicMastheadProperties={
                                setDynamicMastheadProperties
                            }
                            selectedDynamicMasthead={selectedDynamicMasthead}
                            setSelectedDynamicMasthead={
                                setSelectedDynamicMasthead
                            }
                            isMobile={isMobile}
                            setIsLoading={setIsLoading}
                            fromAddVehicleParameter={!!hasAddVehicleParameter}
                            fromConnectedServices={
                                !!hasConnectedServicesParameter
                            }
                            currentVehiclesCount={currentVehiclesCount}
                            setCurrentVehiclesCount={setCurrentVehiclesCount}
                            vehiclesOnOrder={vehiclesOnOrder}
                            vehiclesData={vehiclesData}
                            setVehiclesData={setVehiclesData}
                            deletedVehicleState={deletedVehicleState}
                            setDeletedVehicleState={setDeletedVehicleState}
                            deliveredVehicles={deliveredVehicles}
                            vehicleCarouselContent={vehicleCarouselContent}
                            mastheadContent={mastheadContent}
                            activeNvcOrders={activeNvcOrders}
                            hasElectricVehicle={userHasElectricVehicle}
                            setTeslaBannerNotificationStatus={
                                setChargerAdapterNotificationStatus
                            }
                            notificationBannerContent={
                                notificationBannerContent
                            }
                        />
                    }
                    isMobile={isMobile}
                    setTeslaBannerNotificationStatus={
                        setChargerAdapterNotificationStatus
                    }
                />
            )}
            {profileError.status && <ErrorPage />}
        </>
    );
};

export default AccountDashboardView;
